// frontend/src/context/ChatContext.js

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';

import { useAuth } from './AuthContext';
import api, { userStatusAPI } from '../services/api';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [openChats, setOpenChats] = useState([]);
  const [messages, setMessages] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [onlineUsersData, setOnlineUsersData] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prevent stale closure issue
  const openChatsRef = useRef(openChats);

  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);

  // 🔹 Poll Conversations
  useEffect(() => {
    if (!user?.id) return;

    let interval;

    const fetchConversations = async () => {
      try {
        const res = await api.get('/conversations');

        const data = res.data?.data || res.data || [];

        setConversations(data);

        // Calculate unread count
        const openChatIds = openChatsRef.current.map(c => c.id);

        const total = data.reduce((sum, c) => {
          if (openChatIds.includes(c.id)) return sum;
          return sum + (c.unread_count || 0);
        }, 0);

        setUnreadTotal(total);

      } catch (err) {
        console.warn('Conversation poll failed', err);
      }
    };

    fetchConversations();

    interval = setInterval(fetchConversations, 15000);

    return () => clearInterval(interval);

  }, [user?.id]);

  // 🔹 Poll Messages
  useEffect(() => {
    if (!user?.id || openChats.length === 0) return;

    const intervals = [];

    openChats.forEach((chat) => {

      const interval = setInterval(async () => {

        try {
          const res = await api.get(`/conversations/${chat.id}`);

          const msgData = (
            res.data?.messages?.data ||
            res.data?.messages ||
            []
          ).slice().reverse();

          setMessages(prev => ({
            ...prev,
            [chat.id]: msgData
          }));

          // Mark as read if not minimized
          if (!chat.minimized) {
            await api
              .post(`/conversations/${chat.id}/read`)
              .catch(() => { });
          }

        } catch (err) {
          console.warn(`Message poll failed for ${chat.id}`, err);
        }

      }, 4000);

      intervals.push(interval);

    });

    return () => intervals.forEach(clearInterval);

  }, [user?.id, openChats.map(c => c.id).join(',')]);

  // 🔹 Poll Online Users
  useEffect(() => {
    if (!user?.id) return;

    let interval;

    const fetchOnline = async () => {
      try {
        const res = await api.get('/users/online');

        const raw = res.data || {};

        const userArray = Array.isArray(raw)
          ? raw
          : Object.values(raw);

        // Store all user data
        setOnlineUsersData(userArray);

        // Store only online user IDs
        setOnlineUsers(
          new Set(
            userArray
              .filter(
                (u) =>
                  u.is_online === true ||
                  u.status === 'online'
              )
              .map((u) => u.id)
          )
        );

      } catch (err) {
        console.warn('Online poll failed', err);
      }
    };

    fetchOnline();

    interval = setInterval(fetchOnline, 10000);

    return () => clearInterval(interval);

  }, [user?.id]);

  // 🔹 Set Online / Offline
  useEffect(() => {
    if (!user?.id) return;

    // Immediately set online
    userStatusAPI.update('online').catch(console.error);

    const handleUnload = () => {
      userStatusAPI.update('offline');
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };

  }, [user?.id]);

  // 🔄 Keep user online alive
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      userStatusAPI.update('online').catch(() => { });
    }, 30000);

    return () => clearInterval(interval);

  }, [user?.id]);

  // ✅ Send Message
  const sendMessage = useCallback(async (convId, content, file = null) => {

    if (!convId || (!content?.trim() && !file)) return;

    try {
      const fd = new FormData();

      if (content?.trim()) {
        fd.append('body', content.trim());
      }

      if (file) {
        fd.append('file', file);
      }

      const res = await api.post(
        `/conversations/${convId}/messages`,
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const newMsg = res.data?.message || res.data;

      setMessages(prev => ({
        ...prev,
        [convId]: [...(prev[convId] || []), newMsg]
      }));

      setConversations(prev =>
        prev.map(c =>
          c.id === convId
            ? { ...c, last_message: newMsg }
            : c
        )
      );

      return newMsg;

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send');
      throw err;
    }

  }, []);

  // ✅ Open Chat
  const openChat = useCallback(async (conversationId) => {

    try {

      // Load messages instantly
      const res = await api.get(`/conversations/${conversationId}`);

      const msgData = (
        res.data?.messages?.data ||
        res.data?.messages ||
        []
      ).slice().reverse();

      setMessages(prev => ({
        ...prev,
        [conversationId]: msgData
      }));

      // Open chat window
      setOpenChats(prev => {

        const existing = prev.find(c => c.id === conversationId);

        if (existing) {
          return focusChatInternal(prev, conversationId);
        }

        const maxZ =
          prev.length > 0
            ? Math.max(...prev.map(c => c.zIndex))
            : 1299;

        return [
          ...prev,
          {
            id: conversationId,
            minimized: false,
            zIndex: maxZ + 1
          }
        ];

      });

    } catch (err) {
      console.warn('Failed to open chat', err);
    }

  }, []);

  // ✅ Load Messages
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      const res = await api.get(`/conversations/${conversationId}`);

      const msgData = (
        res.data?.messages?.data ||
        res.data?.messages ||
        []
      ).slice().reverse();

      setMessages(prev => ({
        ...prev,
        [conversationId]: msgData
      }));

      // Open chat window
      setOpenChats(prev => {
        const existing = prev.find(c => c.id === conversationId);

        if (existing) {
          return focusChatInternal(prev, conversationId);
        }

        const maxZ =
          prev.length > 0
            ? Math.max(...prev.map(c => c.zIndex))
            : 1299;

        return [
          ...prev,
          {
            id: conversationId,
            minimized: false,
            zIndex: maxZ + 1
          }
        ];
      });

      // mark read
      await api.post(`/conversations/${conversationId}/read`).catch(() => { });

    } catch (err) {
      console.error('Load messages failed', err);
      throw err;
    }
  }, []);


  // ✅ Close Chat
  const closeChat = useCallback((conversationId) => {

    setOpenChats(prev =>
      prev.filter(c => c.id !== conversationId)
    );

  }, []);

  // ✅ Minimize Chat
  const toggleMinimize = useCallback((conversationId) => {

    setOpenChats(prev =>
      prev.map(c =>
        c.id === conversationId
          ? { ...c, minimized: !c.minimized }
          : c
      )
    );

  }, []);

  // ✅ Focus Chat
  const focusChat = useCallback((conversationId) => {

    setOpenChats(prev =>
      focusChatInternal(prev, conversationId)
    );

  }, []);

  // Helper
  const focusChatInternal = (chats, conversationId) => {

    const maxZ =
      chats.length > 0
        ? Math.max(...chats.map(c => c.zIndex))
        : 1299;

    return chats.map(c =>
      c.id === conversationId
        ? { ...c, zIndex: maxZ + 1 }
        : c
    );
  };

  // ✅ Mark Read
  const markAsRead = useCallback(async (conversationId) => {

    try {
      await api.post(`/conversations/${conversationId}/read`);

      setConversations(prev =>
        prev.map(c =>
          c.id === conversationId
            ? { ...c, unread_count: 0 }
            : c
        )
      );

    } catch (err) {
      console.warn('Mark as read failed', err);
    }

  }, []);


  // ✅ Get Unread Count
  const getUnreadCount = useCallback((conversationId) => {

    const conv = conversations.find(c => c.id === conversationId);

    return conv?.unread_count || 0;

  }, [conversations]);

  // ✅ Is Chat Open
  const isChatOpen = useCallback((conversationId) => {

    return openChats.some(c => c.id === conversationId);

  }, [openChats]);

  const value = {
    conversations,
    messages,
    onlineUsers,
    onlineUsersData,
    unreadTotal,
    loading,
    error,

    open,
    setOpen,

    openChats,
    loadMessages,
    openChat,
    closeChat,
    toggleMinimize,
    focusChat,
    isChatOpen,
    getUnreadCount,
    sendMessage,
    markAsRead,
    setError
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);

  if (!ctx) {
    throw new Error('useChat must be used within ChatProvider');
  }

  return ctx;
};
